import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useHealthMode } from '../../context/HealthModeContext';
import ImpersonationModal from '../ImpersonationModal';
import ThemeToggle from '../ThemeToggle';
import HealthToggle from '../HealthToggle';
import SideMenu from '../SideMenu';
import { useInstallPrompt } from '../../hooks/usePWA';
import './Header.css';

interface HeaderProps {
  healthMode?: 'headaches' | 'tensions' | 'lloros';
  onHealthModeChange?: (mode: 'headaches' | 'tensions' | 'lloros') => void;
  showHealthToggle?: boolean;
  // Dashboard navigation props
  currentView?: 'dashboard' | 'analytics';
  onViewChange?: (view: 'dashboard' | 'analytics') => void;
  showDashboardNav?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  healthMode: propHealthMode = 'headaches',
  onHealthModeChange,
  showHealthToggle = false,
  currentView = 'dashboard',
  onViewChange,
  showDashboardNav = false
}) => {
  const { user, isAuthenticated, logout } = useAuth();
  const { planName } = useSubscription();
  const { healthMode, setHealthMode } = useHealthMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isInstallable, promptInstall } = useInstallPrompt();

  // Plan badge info
  const isFreePlan = planName === 'free';

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserPhotoUrl = () => {
    // Para usuarios de Google, priorizar avatar_url (foto de Google) si no tienen profile_photo personalizada
    if (user?.auth_provider === 'google' || user?.auth_provider === 'both') {
      // Si tienen foto personalizada subida, usar esa
      if (user?.profile_photo) {
        if (user.profile_photo.startsWith('http')) {
          return user.profile_photo;
        }
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || window.location.origin;
        const photoPath = user.profile_photo.startsWith('/') ? user.profile_photo : `/${user.profile_photo}`;
        return `${baseUrl}${photoPath}`;
      }
      // Si no, usar la foto de Google
      if (user?.avatar_url) {
        return user.avatar_url;
      }
    } else {
      // Para usuarios locales, usar profile_photo si existe
      if (user?.profile_photo) {
        if (user.profile_photo.startsWith('http')) {
          return user.profile_photo;
        }
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || window.location.origin;
        const photoPath = user.profile_photo.startsWith('/') ? user.profile_photo : `/${user.profile_photo}`;
        return `${baseUrl}${photoPath}`;
      }
    }
    
    return null;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const handleInstallApp = async () => {
    const success = await promptInstall();
    if (success) {
      setShowUserDropdown(false);
    }
  };

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Bloquear scroll del body cuando el modal de impersonar está abierto
  useEffect(() => {
    if (showImpersonationModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showImpersonationModal]);

  return (
    <>
    <header className="header">
      <div className="header-container">
        {/* Left section with hamburger + logo */}
        <div className="header-left">
          {/* Hamburger Menu Button - only show when we have dashboard nav or health toggle */}
          {isAuthenticated && (showDashboardNav || showHealthToggle) && (
            <button 
              className="hamburger-menu-btn"
              onClick={() => setIsSideMenuOpen(true)}
              aria-label="Abrir menú"
            >
              <span className="hamburger-icon">☰</span>
            </button>
          )}
          
          <Link to="/" className="logo">
            MalDolor
          </Link>
        </div>

        {/* Center navigation */}
        <nav className="nav">
          {isAuthenticated ? (
            <>
              {/* Regular dashboard link when not on dashboard page */}
              {!showDashboardNav && (
                <Link 
                  to="/dashboard" 
                  className={`nav-link ${isActiveRoute('/dashboard') ? 'active' : ''}`}
                >
                  📊 Dashboard
                </Link>
              )}
              
              {/* Health Mode Switch */}
              <div className="health-mode-switch">
                <button 
                  className={`health-mode-btn ${healthMode === 'headaches' ? 'active' : ''}`}
                  onClick={() => setHealthMode('headaches')}
                >
                  🤕 Dolores
                </button>
                <button 
                  className={`health-mode-btn ${healthMode === 'tensions' ? 'active' : ''}`}
                  onClick={() => setHealthMode('tensions')}
                >
                  💓 Tensiones
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link">
                Iniciar Sesión
              </Link>
              <Link to="/register" className="nav-link register-link">
                Registrarse
              </Link>
            </>
          )}
        </nav>

        {/* User Menu - Desktop */}
        {isAuthenticated && (
          <div className="user-dropdown" ref={dropdownRef}>
            <button className="user-dropdown-trigger" onClick={toggleUserDropdown}>
              <div className="user-avatar">
                {getUserPhotoUrl() ? (
                  <img 
                    src={getUserPhotoUrl()!} 
                    alt="Avatar" 
                    className="user-avatar-image"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (nextElement) nextElement.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="user-avatar-initials" 
                  style={{ display: getUserPhotoUrl() ? 'none' : 'flex' }}
                >
                  {getUserInitials(user?.name || '')}
                </div>
              </div>
              <span className="user-name-compact">
                {user?.name?.split(' ')[0]}
              </span>
              <span className="dropdown-arrow">▼</span>
            </button>
            
            {showUserDropdown && (
              <div className="user-dropdown-menu">
                {/* User Info Header */}
                <div className="user-info-header">
                  <div className="user-avatar-large">
                    {getUserPhotoUrl() ? (
                      <img 
                        src={getUserPhotoUrl()!} 
                        alt="Avatar" 
                        className="user-avatar-image-large"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) nextElement.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="user-avatar-initials-large" 
                      style={{ display: getUserPhotoUrl() ? 'none' : 'flex' }}
                    >
                      {getUserInitials(user?.name || '')}
                    </div>
                  </div>
                  <div className="user-details">
                    <div className="user-full-name">{user?.name}</div>
                    <div className="user-email">{user?.email}</div>
                  </div>
                </div>
                <div className="dropdown-separator"></div>
                
                <Link to="/profile" className="dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  👤 Perfil
                </Link>
                <Link to="/subscription" className="dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  ⭐ Suscripción
                </Link>
                {(user?.is_admin || user?.admin_role) && (
                  <Link to="/admin" className="dropdown-item" onClick={() => setShowUserDropdown(false)}>
                    ⚡ Admin
                  </Link>
                )}
                {(user?.is_admin || user?.admin_role) && !(user as any)?.isImpersonating && (
                  <button 
                    onClick={() => {
                      setShowImpersonationModal(true);
                      setShowUserDropdown(false);
                    }} 
                    className="dropdown-item dropdown-button"
                  >
                    👥 Impersonar
                  </button>
                )}
                <div className="dropdown-separator"></div>
                <div className="dropdown-item theme-toggle-wrapper">
                  <span>🌙 Modo oscuro</span>
                  <ThemeToggle className="compact" />
                </div>
                {isInstallable && (
                  <>
                    <div className="dropdown-separator"></div>
                    <button onClick={handleInstallApp} className="dropdown-item dropdown-button">
                      📱 Instalar App
                    </button>
                  </>
                )}
                <div className="dropdown-separator"></div>
                <button onClick={handleLogout} className="dropdown-item dropdown-button logout">
                  🚪 Salir
                </button>
              </div>
            )}
          </div>
        )}

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
        {isAuthenticated ? (
          <>
            <div className="mobile-nav-links">
              <Link 
                to="/dashboard" 
                className={`nav-link ${isActiveRoute('/dashboard') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                📊 Dashboard
              </Link>
              
              {/* Health Mode Switch Mobile only */}
              <div className="mobile-health-switch">
                <span className="mobile-switch-label">Modo de seguimiento:</span>
                <div className="health-mode-switch mobile">
                  <button 
                    className={`health-mode-btn ${healthMode === 'headaches' ? 'active' : ''}`}
                    onClick={() => setHealthMode('headaches')}
                  >
                    🤕 Dolores
                  </button>
                  <button 
                    className={`health-mode-btn ${healthMode === 'tensions' ? 'active' : ''}`}
                    onClick={() => setHealthMode('tensions')}
                  >
                    💓 Tensiones
                  </button>
                </div>
              </div>
              <Link 
                to="/profile" 
                className={`nav-link ${isActiveRoute('/profile') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                👤 Perfil
              </Link>
              <Link 
                to="/subscription" 
                className={`nav-link ${isActiveRoute('/subscription') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                ⭐ Suscripción
              </Link>
              {(user?.is_admin || user?.admin_role) && (
                <Link 
                  to="/admin" 
                  className={`nav-link admin-link ${isActiveRoute('/admin') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  ⚡ Admin
                </Link>
              )}
            </div>
            
            {/* Theme Toggle - Mobile */}
            <div className="mobile-theme-section">
              <ThemeToggle showLabel />
            </div>
            
            <div className="mobile-user-section">
              <div className="mobile-user-info">
                <div className="user-avatar">
                  {getUserPhotoUrl() ? (
                    <img 
                      src={getUserPhotoUrl()!} 
                      alt="Avatar" 
                      className="user-avatar-image"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                        if (nextElement) nextElement.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="user-avatar-initials" 
                    style={{ display: getUserPhotoUrl() ? 'none' : 'flex' }}
                  >
                    {getUserInitials(user?.name || '')}
                  </div>
                </div>
                <div className="user-details">
                  <div className="user-name">{user?.name}</div>
                  <div className="user-role">
                    {user?.is_admin || user?.admin_role ? 'Administrador' : 'Usuario'}
                  </div>
                </div>
              </div>
              {(user?.is_admin || user?.admin_role) && !(user as any)?.isImpersonating && (
                <button 
                  onClick={() => {
                    setShowImpersonationModal(true);
                    setIsMobileMenuOpen(false);
                  }} 
                  className="impersonate-btn mobile"
                >
                  👤 Impersonar Usuario
                </button>
              )}
              <button onClick={handleLogout} className="logout-btn">
                👋 Cerrar Sesión
              </button>
            </div>
          </>
        ) : (
          <div className="mobile-nav-links">
            <Link 
              to="/login" 
              className="nav-link"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Iniciar Sesión
            </Link>
            <Link 
              to="/register" 
              className="nav-link register-link"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Registrarse
            </Link>
          </div>
        )}
      </div>

    </header>

      {/* Free Plan Banner */}
      {isAuthenticated && isFreePlan && (
        <div className="trial-banner free-plan-banner">
          <div className="trial-banner-content">
            <div className="trial-banner-icon">🆓</div>
            <div className="trial-banner-text">
              <strong>PLAN GRATUITO</strong>
              <span className="trial-banner-days">
                Limite de 10 registros/mes - Actualiza para registros ilimitados
              </span>
            </div>
            <Link to="/subscription" className="trial-banner-btn">
              Actualizar
            </Link>
          </div>
        </div>
      )}


      {/* Impersonation Modal - FUERA del header */}
      <ImpersonationModal
        isOpen={showImpersonationModal}
        onClose={() => setShowImpersonationModal(false)}
      />
      
      {/* Side Menu */}
      <SideMenu
        isOpen={isSideMenuOpen}
        onClose={() => setIsSideMenuOpen(false)}
      />
    </>
  );
};

export default Header;